import { PermissionType } from "@/pages/Configuration/AdminConfiguration/Users/types/cognitoTypes";
import { faBookOpen, faServer } from "@fortawesome/free-solid-svg-icons";
import { Styles } from "@/shared/coralreef";
import { useRouterState } from "@tanstack/react-router";
import { deg, padding, percent, px, rem, rotate } from "csx";
import {
  Content,
  Horizontal,
  StretchSpacer,
  Vertical,
  VerticalSpacer,
} from "gls/lib";
import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LayoutSearchParams } from "@/shared/types/layout";
import { WINDOW_MEDIUM } from "shared/constants/window";
import { useWindowLimit } from "shared/hooks/useWindowWidthLimit";
import { classes, style, stylesheet } from "typestyle";
import { useStorePagesEnabled } from "@/shared/hooks/useStorePagesEnabled";
import { isEmergencySession } from "@/exported/shared/utils/emergencySession";
import { Icon } from "../icon/Icon";
import { Logo } from "../logo/Logo";
import { SidebarItem } from "./SidebarItem";
import { UserSettings } from "./UserSettings";
import { SideBarAdminConfigMenu } from "./menus/SideBarAdminConfigMenu";
import { SideBarMainMenu } from "./menus/SideBarMainMenu";
import { SideBarSupportMenu } from "./menus/SideBarSupportMenu";

export type SideMenuOptions = "main" | "config" | "support";
export const selectedSideBarMenuAtom = atom<SideMenuOptions>("main");
export const sidebarIsCollapsedAtom = atomWithStorage<boolean>(
  "sidebar_key",
  true,
);

export const Sidebar: FC = () => {
  const { t } = useTranslation();
  const {
    location: { pathname },
  } = useRouterState();

  const [selectedMenu, setSelectedMenu] = useAtom(selectedSideBarMenuAtom);

  const [isCollapsedLocalStorage, setIsCollapsedLocalStorage] = useAtom(
    sidebarIsCollapsedAtom,
  );

  const smallScreen = useWindowLimit(WINDOW_MEDIUM, {
    lowerThan: true,
    equal: true,
  });

  const isCollapsed = useMemo(() => {
    if (selectedMenu !== "main") {
      return false;
    }
    return !!isCollapsedLocalStorage;
  }, [isCollapsedLocalStorage, selectedMenu]);

  const isFloating = useMemo(() => {
    return (
      smallScreen && !isCollapsed && !pathname.includes("admin-configuration")
    );
  }, [isCollapsed, smallScreen, pathname]);

  const storePagesEnabled = useStorePagesEnabled();
  const isEmergency = isEmergencySession();

  return (
    <Vertical
      height={percent(100)}
      spacing={0}
      className={classes(isFloating && style({ width: px(58) }))}
    >
      <Vertical
        width={isCollapsed ? px(58) : px(290)}
        spacing={0}
        height={percent(100)}
        className={classes(
          styles.sidebar,
          isFloating && style({ position: "absolute" }),
        )}
      >
        <Horizontal
          padding={isCollapsed ? [px(8), 0] : [px(8), px(10), px(8), px(16)]}
          horizontalAlign={isCollapsed ? "center" : "left"}
          verticalAlign="center"
        >
          <Logo isCollapsed={isCollapsed} />
          {!isCollapsed && selectedMenu === "main" && (
            <>
              <StretchSpacer />
              <Content data-testid="sidebar-toggle">
                <Icon
                  onClick={() => {
                    setIsCollapsedLocalStorage(true);
                  }}
                  icon="angleDown"
                  className={classes(
                    styles.toggle,
                    style({ transform: rotate(deg(90)) }),
                  )}
                />
              </Content>
            </>
          )}
        </Horizontal>
        {!isCollapsed && <VerticalSpacer space={px(42)} />}
        {isCollapsed && (
          <Horizontal
            height={px(50)}
            verticalAlign="center"
            horizontalAlign="center"
            data-testid="sidebar-toggle"
          >
            <Icon
              onClick={() => {
                setIsCollapsedLocalStorage(false);
              }}
              icon="angleDown"
              className={classes(
                styles.toggle,
                style({ transform: rotate(deg(-90)) }),
              )}
            />
          </Horizontal>
        )}

        {selectedMenu === "main" && (
          <SideBarMainMenu isCollapsed={isCollapsed} />
        )}
        {selectedMenu === "config" && <SideBarAdminConfigMenu />}

        {selectedMenu === "support" && <SideBarSupportMenu />}

        {storePagesEnabled && (
          <Vertical spacing={0}>
            <SidebarItem
              hideTextWhenCollapsed
              isCollapsed={isCollapsed}
              icon={faServer}
              text={t("Support")}
              permissionName={PermissionType.Support}
              onClick={() => setSelectedMenu("support")}
              data-testid="support-menu-button"
            />
            {!isEmergency && (
              <SidebarItem
                hideTextWhenCollapsed
                isCollapsed={isCollapsed}
                icon={faBookOpen}
                text={t("User Manual")}
                navigateTo={"/manual"}
                data-testid="sidebar-item-manual"
              />
            )}
          </Vertical>
        )}

        <UserSettings
          isCollapsed={isCollapsed}
          onMenuChange={setSelectedMenu}
        />
      </Vertical>
    </Vertical>
  );
};

export const defaultSearchParams = (search: LayoutSearchParams) => ({
  sites: search.sites,
  cages: search.cages,
  start: search.start,
  end: search.end,
});

export const styles = stylesheet({
  sidebar: {
    background: Styles.Colours.darkerBlue,
    boxShadow: Styles.boxShadow,
    position: "relative",
    zIndex: 999,
    boxSizing: "border-box",
  },
  toggle: {
    cursor: "pointer",
    padding: padding(px(10), px(6)),
  },
});
